import { PropertyDetailsFields, initialPropertyDetails, PropertyDetails } from "../../components/Property/PropertyDetailsFields"
import VariantAttributeFields from "../../components/Products/VariantAttributeFields"
import { taxonomyApi, ProductTypeDefinition } from "../../api/taxonomy"
import { useEffect, useMemo, useRef, useState } from "react"
import { ArrowLeft, Check, ChevronRight, FileSpreadsheet, Film, LocateFixed, Package, RefreshCcw, Upload } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-hot-toast"
import { createGeneralProduct1, uploadVehicleCarImagesToS3, uploadVehicleVideoToS3 } from "../../api/products/post"
import { getGeneralProductsCatalog } from "../../api/products/get"
import * as XLSX from "xlsx"
import { getCurrentPlan, getMaxProducts } from "../../utils/permissions"
import {
  detectCurrentPropertyLocation,
  fetchNearbyPropertyAttractions,
  parseGoogleMapsLocation,
  parseNearbyAttractions,
  serializeNearbyAttractions,
} from "../../utils/propertyLocation"
import { normalizeAmenityList, suggestPropertyAmenities } from "../../utils/propertyAmenities"
import { getStoreProductRules } from "../../utils/storeCategory"
import { getStoreExperience } from "../../utils/storeExperience"
import { SERVICE_DELIVERY_OPTIONS, SERVICE_PRICING_OPTIONS } from "../../utils/serviceOffering"

type StepId = "identity" | "listing" | "media"
type ImportRow = {
  propertyDetails: PropertyDetails
  rowNumber: number
  name: string
  identifier: string
  buyPrice: number
  sellPrice: number
  quantity: number
  vehicleCategory: string
  listingType: string
  dailyRentalPrice: number
  description: string
  propertyType: string
  district: string
  bedrooms: number
  bathrooms: number
  areaSqm: number
  maxGuests: number
  monthlyRentalPrice: number
  amenities: string[]
  nearbyAttractionsText: string
  furnished: boolean
  latitude: number | null
  longitude: number | null
  year: string
  mileage: string
  color: string
  transmission: string
  fuel: string
  seats: string
  availability: string
  location: string
  condition: string
  sourceName: string
  sourcePhone: string
  brand: string
  productCategory: string
  unitLabel: string
  stockAlertThreshold: number
  supplierName: string
  barcode: string
  storageLocation: string
  lotNumber: string
  expiryDate: string
}

const STEPS: Array<{ id: StepId; label: string; description: string }> = [
  {
    id: "identity",
    label: "Identite",
    description: "Reference, prix et quantite de depart.",
  },
  {
    id: "listing",
    label: "Details",
    description: "Annonce, infos metier et reference interne.",
  },
  {
    id: "media",
    label: "Media",
    description: "Images, video et verification finale.",
  },
]

const VEHICLE_CATEGORY_OPTIONS = [
  {
    value: "OCCASION",
    label: "Occasion",
    description: "Vehicule deja utilise, principalement pour la vente.",
  },
  {
    value: "LOCATION",
    label: "Location",
    description: "Vehicule destine a la location.",
  },
  {
    value: "NEW",
    label: "Nouvelle voiture",
    description: "Vehicule neuf ou zero kilometre.",
  },
] as const

const VEHICLE_CONDITION_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  OCCASION: [
    { value: "Occasion", label: "Occasion" },
    { value: "Tres bon etat", label: "Tres bon etat" },
    { value: "Bon etat", label: "Bon etat" },
    { value: "Revise", label: "Revise" },
  ],
  LOCATION: [
    { value: "Disponible a la location", label: "Disponible a la location" },
    { value: "Location libre", label: "Location libre" },
    { value: "Location avec chauffeur", label: "Location avec chauffeur" },
    { value: "Longue duree", label: "Longue duree" },
  ],
  NEW: [
    { value: "Neuve", label: "Neuve" },
    { value: "Zero km", label: "Zero km" },
    { value: "Jamais utilisee", label: "Jamais utilisee" },
    { value: "Sur commande", label: "Sur commande" },
  ],
}

const normalizeVehicleCategory = (value: string, fallback = "OCCASION") => {
  const normalized = String(value || "").trim().toUpperCase()
  return VEHICLE_CATEGORY_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : fallback
}

const inferVehicleCategory = (listingType: string, condition: string) => {
  const normalizedListingType = String(listingType || "").trim().toUpperCase()
  const normalizedCondition = String(condition || "").trim().toLowerCase()

  if (normalizedListingType === "RENT") {
    return "LOCATION"
  }

  if (["neuve", "new", "zero km", "0 km", "jamais utilisee", "jamais utilise"].some((hint) => normalizedCondition.includes(hint))) {
    return "NEW"
  }

  return "OCCASION"
}

export default function AddProductPage() {
  const { userdata } = useSelector((state: any) => state.UserReducer)
  const currentStore = userdata?.store
  const currentPlan = getCurrentPlan(userdata)
  const experience = getStoreExperience(currentStore)
  const isGymStore = experience.kind === "gym"
  const isServiceStore = experience.kind === "service"
  const navigate = useNavigate()
  const location = useLocation()

  const [name, setName] = useState("")
  const [code, setCode] = useState("")
  const [buyPrice, setBuyPrice] = useState("")
  const [sellPrice, setSellPrice] = useState("")
  /* Structured product attributes (size / pointure / colour / material), driven
     by the store's product types. See config/productTaxonomy.js. */
  const [variantAttributes, setVariantAttributes] = useState<Record<string, string | number>>({})
  /**
   * Product type driving the structured attributes.
   *
   * Priority: an explicit type set by the merchant > the taxonomy's keyword match
   * on the product name > the store's only allowed type. Kept as state so the
   * merchant can override a wrong guess.
   */
  const [productTypeOverride, setProductTypeOverride] = useState("")
  const productType = productTypeOverride
  const [variantName, setVariantName] = useState("")
  const [familyName, setFamilyName] = useState("")
  const [quantity, setQuantity] = useState("1")
  const [listingType, setListingType] = useState("SALE")
  const [vehicleCategory, setVehicleCategory] = useState("OCCASION")
  const [dailyRentalPrice, setDailyRentalPrice] = useState("")
  const [description, setDescription] = useState("")
  const [propertyType, setPropertyType] = useState("APPARTEMENT")
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails>({...initialPropertyDetails})
  const [district, setDistrict] = useState("")
  const [bedrooms, setBedrooms] = useState("")
  const [bathrooms, setBathrooms] = useState("")
  const [areaSqm, setAreaSqm] = useState("")
  const [maxGuests, setMaxGuests] = useState("")
  const [monthlyRentalPrice, setMonthlyRentalPrice] = useState("")
  const [amenities, setAmenities] = useState("")
  const [nearbyAttractionsText, setNearbyAttractionsText] = useState("")
  const [furnished, setFurnished] = useState(true)
  const [latitude, setLatitude] = useState("")
  const [longitude, setLongitude] = useState("")
  const [mapUrl, setMapUrl] = useState("")
  const [detectingLocation, setDetectingLocation] = useState(false)
  const [loadingNearbyAttractions, setLoadingNearbyAttractions] = useState(false)
  const [year, setYear] = useState("")
  const [mileage, setMileage] = useState("")
  const [color, setColor] = useState("")
  const [transmission, setTransmission] = useState("")
  const [fuel, setFuel] = useState("")
  const [seats, setSeats] = useState("")
  const [availability, setAvailability] = useState("")
  const [locationField, setLocationField] = useState("")
  const [condition, setCondition] = useState("")
  const [sourceName, setSourceName] = useState("")
  const [sourcePhone, setSourcePhone] = useState("")
  const [brand, setBrand] = useState("")
  const [productCategory, setProductCategory] = useState("")
  const [pricingType, setPricingType] = useState("FIXED")
  const [startingPrice, setStartingPrice] = useState("")
  const [durationMinutes, setDurationMinutes] = useState("")
  const [deliveryMode, setDeliveryMode] = useState("ONSITE")
  const [serviceArea, setServiceArea] = useState(String(currentStore?.serviceArea || ""))
  const [serviceTags, setServiceTags] = useState("")
  const [bookingEnabled, setBookingEnabled] = useState(true)
  const [featured, setFeatured] = useState(false)
  const [unitLabel, setUnitLabel] = useState("")
  const [stockAlertThreshold, setStockAlertThreshold] = useState("")
  const [supplierName, setSupplierName] = useState("")
  const [barcode, setBarcode] = useState("")
  const [storageLocation, setStorageLocation] = useState("")
  const [lotNumber, setLotNumber] = useState("")
  const [expiryDate, setExpiryDate] = useState("")
  const [carImageFiles, setCarImageFiles] = useState<File[]>([])
  const [carImagePreviewUrls, setCarImagePreviewUrls] = useState<string[]>([])
  const [vehicleVideoFile, setVehicleVideoFile] = useState<File | null>(null)
  const [vehicleVideoPreviewUrl, setVehicleVideoPreviewUrl] = useState("")
  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [generatingReference, setGeneratingReference] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [currentProductsCount, setCurrentProductsCount] = useState(
    Number((location.state as any)?.currentProductsCount || 0)
  )
  const [importRows, setImportRows] = useState<ImportRow[]>([])
  const [importFileName, setImportFileName] = useState("")
  const [importSummary, setImportSummary] = useState<{
    successCount: number
    failureCount: number
    failures: string[]
  } | null>(null)
  const stepContentRef = useRef<HTMLDivElement | null>(null)
  const stepButtonRefs = useRef<Array<HTMLButtonElement | null>>([])

  const {
    isCarStore,
    isPropertyStore,
    isMediaCatalogStore,
    itemLabel,
    imageLabel,
    minimumImages,
  } = getStoreProductRules(currentStore)
  const isRentalListing = listingType === "RENT"
  const requiresBuyPrice = !isPropertyStore && !isRentalListing && !isServiceStore
  const requiresSellPrice = !isPropertyStore && !isRentalListing && !isServiceStore
  const requiresRentalPrice = isMediaCatalogStore && listingType !== "SALE"
  const acceptsSingleOptionalImage = !isMediaCatalogStore
  const isGymListing = /(gym|fitness|crossfit|workout|training|musculation|wellness|studio|club)/i.test(productCategory)
  const isFreePlan = String(currentPlan || "FREE").toUpperCase() === "FREE"
  const freeLimitReached = isFreePlan && Number(currentProductsCount || 0) >= 20
  const currentStepId = STEPS[activeStep]?.id
  const maxProducts = getMaxProducts(currentPlan)
  const canUseExcelImport = !loading && !importing
  const vehicleConditionOptions = VEHICLE_CONDITION_OPTIONS[vehicleCategory] || VEHICLE_CONDITION_OPTIONS.OCCASION
  const carListingTypeOptions =
    vehicleCategory === "LOCATION"
      ? [
          { value: "RENT", label: "Location" },
          { value: "BOTH", label: "Location & Vente" },
        ]
      : [
          { value: "SALE", label: "Vente" },
          { value: "BOTH", label: "Vente & Location" },
        ]

  const pageTitle = isCarStore ? "Nouveau vehicule" : isPropertyStore ? "Nouveau bien" : isServiceStore ? "Nouveau service" : "Nouveau produit"
  const pageDescription = isCarStore
    ? "Ajoutez un vehicule avec une navigation par etapes, puis revenez au parc auto."
    : isPropertyStore
      ? "Ajoutez un bien avec une saisie structuree et un resume avant publication."
      : isServiceStore
        ? "Ajoutez une prestation avec sa logique de prix, de devis et de zone d'intervention."
        : "Ajoutez un produit dans un flux plus clair, sans modal."

  const applyGymPreset = () => {
    setProductCategory("Gym / Fitness")
    setUnitLabel("membership")
    setAvailability("OUI")

    if (!amenities.trim()) {
      setAmenities("Strength zone, Cardio zone, Showers, Coach")
    }

    if (!description.trim()) {
      setDescription("Salle de sport avec espaces cardio, musculation et accompagnement coaching.")
    }
  }

  const handleSuggestPropertyAmenities = () => {
    const merged = [
      ...normalizeAmenityList(amenities),
      ...suggestPropertyAmenities({
        amenities,
        areaSqm,
        bathrooms,
        bedrooms,
        furnished,
        listingType,
        maxGuests,
        propertyType,
      }),
    ]

    const deduped = Array.from(new Set(merged.map((item) => item.trim()).filter(Boolean)))
    setAmenities(deduped.join(", "))
    toast.success("Equipements suggeres. Ajustez la liste avant enregistrement.")
  }

  const buildShortcut = (value: string, fallback = "PRD") => {
    const ignoredWords = new Set(["STORE", "BOUTIQUE", "SHOP", "MAGASIN"])
    const words = String(value || "")
      .trim()
      .split(/\s+/)
      .map((word) => word.replace(/[^a-zA-Z0-9]/g, ""))
      .filter(Boolean)
      .filter((word) => !ignoredWords.has(word.toUpperCase()))

    const base = (words[0] || "").toUpperCase()
    if (!base) return fallback

    const first = base[0]
    const consonants = base
      .slice(1)
      .replace(/[AEIOUY0-9]/g, "")
      .slice(0, 2)

    const vowelsFallback = base
      .slice(1)
      .replace(/[^AEIOUY0-9]/g, "")

    return `${first}${consonants}${vowelsFallback}`.slice(0, 3).padEnd(3, "X")
  }

  const readCell = (row: Record<string, any>, aliases: string[]) => {
    for (const alias of aliases) {
      const entry = Object.entries(row).find(
        ([key]) => String(key || "").trim().toLowerCase() === alias.toLowerCase()
      )
      if (entry && entry[1] !== undefined && entry[1] !== null && String(entry[1]).trim() !== "") {
        return entry[1]
      }
    }
    return ""
  }

  const toNumberValue = (value: any, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback
    const numeric = Number(String(value).replace(/[^0-9.,-]/g, "").replace(",", "."))
    return Number.isFinite(numeric) ? numeric : fallback
  }

  const toBooleanValue = (value: any, fallback = false) => {
    const normalized = String(value ?? "").trim().toLowerCase()
    if (!normalized) return fallback
    return ["true", "oui", "yes", "1", "meuble", "furnished"].includes(normalized)
  }

  const normalizeImportRow = (row: Record<string, any>, rowNumber: number): ImportRow | null => {
    const nameValue = String(readCell(row, ["name", "nom", "produit", "product", "designation"])).trim()
    if (!nameValue) return null

    const listingTypeValue = String(
      readCell(row, ["listingtype", "typeannonce", "type_annonce", "transactiontype"])
    )
      .trim()
      .toUpperCase() || "SALE"

    return {
      rowNumber,
      name: nameValue,
      identifier: String(readCell(row, ["identifier", "reference", "code", "sku"])).trim().toUpperCase(),
      buyPrice: toNumberValue(readCell(row, ["buyprice", "prixachat", "prix_achat", "achat"])),
      sellPrice: toNumberValue(readCell(row, ["sellprice", "prixvente", "prix_vente", "vente", "price"])),
      quantity: Math.max(1, toNumberValue(readCell(row, ["quantity", "quantite", "qty"]), 1)),
      vehicleCategory: normalizeVehicleCategory(
        String(readCell(row, ["vehiclecategory", "categorievehicule", "categorie_vehicule"])).trim(),
        inferVehicleCategory(listingTypeValue, String(readCell(row, ["condition", "etat"])).trim())
      ),
      listingType: ["SALE", "RENT", "BOTH"].includes(listingTypeValue) ? listingTypeValue : "SALE",
      dailyRentalPrice: toNumberValue(readCell(row, ["dailyrentalprice", "prixlocationjour", "prix_location_jour"])),
      description: String(readCell(row, ["description", "details"])).trim(),
      propertyType: String(readCell(row, ["propertytype", "typedebien", "type_bien"])).trim().toUpperCase() || "APPARTEMENT",
      district: String(readCell(row, ["district", "commune", "quartier"])).trim(),
      bedrooms: toNumberValue(readCell(row, ["bedrooms", "chambres"])),
      bathrooms: toNumberValue(readCell(row, ["bathrooms", "sallesdebain", "sdb"])),
      areaSqm: toNumberValue(readCell(row, ["areasqm", "surface", "surface_m2", "m2"])),
      maxGuests: toNumberValue(readCell(row, ["maxguests", "voyageursmax", "capacite"])),
      monthlyRentalPrice: toNumberValue(readCell(row, ["monthlyrentalprice", "prixlocationmois", "prix_location_mois"])),
      amenities: String(readCell(row, ["amenities", "equipements"])).split(",").map((item) => item.trim()).filter(Boolean),
      nearbyAttractionsText: String(readCell(row, ["nearbyattractions", "attractions", "proximite"])).trim(),
      propertyDetails: {
        publicationStatus: String(readCell(row, ["publicationstatus", "propertydetails.publicationstatus"])).trim() || "DRAFT",
        listingSection: String(readCell(row, ["listingsection", "propertydetails.listingsection"])).trim() || "AUTO",
        residenceCategory: String(readCell(row, ["residencecategory", "propertydetails.residencecategory"])).trim(),
        maisonCategory: String(readCell(row, ["maisoncategory", "propertydetails.maisoncategory"])).trim(),
        terrainCategory: String(readCell(row, ["terraincategory", "propertydetails.terraincategory"])).trim(),
        shortDescription: String(readCell(row, ["shortdescription", "propertydetails.shortdescription"])).trim(),
        coverLabel: String(readCell(row, ["coverlabel", "propertydetails.coverlabel"])).trim(),
        badges: String(readCell(row, ["badges", "propertydetails.badges"])).trim() .split(",").map(value => value.trim()).filter(Boolean),
        city: String(readCell(row, ["city", "propertydetails.city"])).trim(),
        commune: String(readCell(row, ["commune", "propertydetails.commune"])).trim(),
        address: String(readCell(row, ["address", "propertydetails.address"])).trim(),
        landAreaSqm: String(readCell(row, ["landareasqm", "propertydetails.landareasqm"])).trim(),
        rooms: String(readCell(row, ["rooms", "propertydetails.rooms"])).trim(),
        floor: String(readCell(row, ["floor", "propertydetails.floor"])).trim(),
        floors: String(readCell(row, ["floors", "propertydetails.floors"])).trim(),
        constructionYear: String(readCell(row, ["constructionyear", "propertydetails.constructionyear"])).trim(),
        parkingSpaces: String(readCell(row, ["parkingspaces", "propertydetails.parkingspaces"])).trim(),
        pricePerM2: String(readCell(row, ["priceperm2", "propertydetails.priceperm2"])).trim(),
        weeklyRentalPrice: String(readCell(row, ["weeklyrentalprice", "propertydetails.weeklyrentalprice"])).trim(),
        securityDeposit: String(readCell(row, ["securitydeposit", "propertydetails.securitydeposit"])).trim(),
        cleaningFee: String(readCell(row, ["cleaningfee", "propertydetails.cleaningfee"])).trim(),
        agencyFee: String(readCell(row, ["agencyfee", "propertydetails.agencyfee"])).trim(),
        advanceMonths: String(readCell(row, ["advancemonths", "propertydetails.advancemonths"])).trim(),
        minimumNights: String(readCell(row, ["minimumnights", "propertydetails.minimumnights"])).trim(),
        availableFrom: String(readCell(row, ["availablefrom", "propertydetails.availablefrom"])).trim(),
        checkIn: String(readCell(row, ["checkin", "propertydetails.checkin"])).trim(),
        checkOut: String(readCell(row, ["checkout", "propertydetails.checkout"])).trim(),
        rentalTerms: String(readCell(row, ["rentalterms", "propertydetails.rentalterms"])).trim(),
        houseRules: String(readCell(row, ["houserules", "propertydetails.houserules"])).trim(),
        petsAllowed: String(readCell(row, ["petsallowed", "propertydetails.petsallowed"])).trim(),
        smokingAllowed: String(readCell(row, ["smokingallowed", "propertydetails.smokingallowed"])).trim(),
        landTitle: String(readCell(row, ["landtitle", "propertydetails.landtitle"])).trim(),
        zoning: String(readCell(row, ["zoning", "propertydetails.zoning"])).trim(),
        roadAccess: String(readCell(row, ["roadaccess", "propertydetails.roadaccess"])).trim(),
        waterConnected: String(readCell(row, ["waterconnected", "propertydetails.waterconnected"])).trim(),
        electricityConnected: String(readCell(row, ["electricityconnected", "propertydetails.electricityconnected"])).trim(),
        video360Url: String(readCell(row, ["video360url", "propertydetails.video360url"])).trim(),
        videoUrl: String(readCell(row, ["videourl", "propertydetails.videourl"])).trim(),
        floorPlanUrl: String(readCell(row, ["floorplanurl", "propertydetails.floorplanurl"])).trim(),
        publicContactName: String(readCell(row, ["publiccontactname", "propertydetails.publiccontactname"])).trim(),
        publicContactRole: String(readCell(row, ["publiccontactrole", "propertydetails.publiccontactrole"])).trim(),
        publicPhone: String(readCell(row, ["publicphone", "propertydetails.publicphone"])).trim(),
        publicWhatsapp: String(readCell(row, ["publicwhatsapp", "propertydetails.publicwhatsapp"])).trim(),
      },
      furnished: toBooleanValue(readCell(row, ["furnished", "meuble"])),
      latitude: readCell(row, ["latitude", "lat"]) === "" ? null : toNumberValue(readCell(row, ["latitude", "lat"])),
      longitude: readCell(row, ["longitude", "lng", "long"]) === "" ? null : toNumberValue(readCell(row, ["longitude", "lng", "long"])),
      year: String(readCell(row, ["year", "annee"])).trim(),
      mileage: String(readCell(row, ["mileage", "kilometrage"])).trim(),
      color: String(readCell(row, ["color", "couleur"])).trim(),
      transmission: String(readCell(row, ["transmission"])).trim(),
      fuel: String(readCell(row, ["fuel", "carburant"])).trim(),
      seats: String(readCell(row, ["seats", "places"])).trim(),
      availability: String(readCell(row, ["availability", "disponibilite"])).trim().toUpperCase(),
      location: String(readCell(row, ["location", "localisation", "adresse"])).trim(),
      condition: String(readCell(row, ["condition", "etat"])).trim(),
      sourceName: String(readCell(row, ["sourcename", "proprietaire", "source"])).trim(),
      sourcePhone: String(readCell(row, ["sourcephone", "telephoneproprietaire", "phone_source"])).trim(),
      brand: String(readCell(row, ["brand", "marque"])).trim(),
      productCategory: String(readCell(row, ["productcategory", "categorie", "category"])).trim(),
      unitLabel: String(readCell(row, ["unitlabel", "unite", "unit"])).trim(),
      stockAlertThreshold: toNumberValue(readCell(row, ["stockalertthreshold", "seuilalerte", "alertestock"])),
      supplierName: String(readCell(row, ["suppliername", "fournisseur"])).trim(),
      barcode: String(readCell(row, ["barcode", "codebarres", "ean", "sku_secondaire"])).trim(),
      storageLocation: String(readCell(row, ["storagelocation", "emplacement", "rayon"])).trim(),
      lotNumber: String(readCell(row, ["lotnumber", "lot"])).trim(),
      expiryDate: String(readCell(row, ["expirydate", "expiration", "dateexpiration"])).trim(),
    }
  }

  useEffect(() => {
    if (!carImageFiles.length) {
      setCarImagePreviewUrls([])
      return
    }

    const localPreviews = carImageFiles.map((file) => URL.createObjectURL(file))
    setCarImagePreviewUrls(localPreviews)

    return () => {
      localPreviews.forEach((preview) => URL.revokeObjectURL(preview))
    }
  }, [carImageFiles])

  useEffect(() => {
    if (!vehicleVideoFile) {
      setVehicleVideoPreviewUrl("")
      return
    }

    const localPreview = URL.createObjectURL(vehicleVideoFile)
    setVehicleVideoPreviewUrl(localPreview)

    return () => {
      URL.revokeObjectURL(localPreview)
    }
  }, [vehicleVideoFile])

  useEffect(() => {
    if (!isCarStore) return

    const allowedListingTypes =
      vehicleCategory === "LOCATION" ? ["RENT", "BOTH"] : ["SALE", "BOTH"]

    if (!allowedListingTypes.includes(listingType)) {
      setListingType(vehicleCategory === "LOCATION" ? "RENT" : "SALE")
    }

    if (!vehicleConditionOptions.some((option) => option.value === condition)) {
      setCondition(vehicleConditionOptions[0]?.value || "")
    }
  }, [condition, isCarStore, listingType, vehicleCategory, vehicleConditionOptions])

  useEffect(() => {
    if (!isServiceStore) return
    setServiceArea(String(currentStore?.serviceArea || ""))
  }, [currentStore?.serviceArea, isServiceStore])

  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 768) return

    if (stepContentRef.current) {
      const top = stepContentRef.current.getBoundingClientRect().top + window.scrollY - 88
      window.scrollTo({
        top: Math.max(0, top),
        behavior: "smooth",
      })
    }

    stepButtonRefs.current[activeStep]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    })
  }, [activeStep])

  useEffect(() => {
    if ((location.state as any)?.currentProductsCount !== undefined) return
    if (!currentStore?._id) return

    let isMounted = true

    const fetchCount = async () => {
      try {
        const catalog = await getGeneralProductsCatalog(currentStore._id, "")
        const products = Array.isArray(catalog) ? catalog : catalog?.data || catalog?.items || []
        if (isMounted) {
          setCurrentProductsCount(Array.isArray(products) ? products.length : 0)
        }
      } catch {
        // keep fallback value if count cannot be loaded
      }
    }

    fetchCount()

    return () => {
      isMounted = false
    }
  }, [currentStore?._id, location.state])

  const getNextStoredSequence = async () => {
    if (!currentStore?._id) return 1

    const catalog = await getGeneralProductsCatalog(currentStore._id, "")
    const products = Array.isArray(catalog) ? catalog : catalog?.data || catalog?.items || []

    const maxSequence = products.reduce((highest: number, item: any) => {
      const identifier = String(
        item?.identifier || item?.sku || item?.code || item?.imei || ""
      ).trim()

      const match = identifier.match(/-(\d+)$/)
      if (!match) return highest

      const sequence = Number(match[1])
      if (!Number.isFinite(sequence)) return highest

      return Math.max(highest, sequence)
    }, 0)

    return maxSequence + 1
  }

  const generateReference = async () => {
    if (!name.trim()) {
      const msg = "Entrez d'abord le nom du produit pour générer la référence."
      setError(msg)
      toast.error(msg)
      return
    }

    try {
      setGeneratingReference(true)
      setError("")

      const storeCode = buildShortcut(currentStore?.name, "STR")
      const productCode = buildShortcut(name, "PRD")
      const nextSequence = await getNextStoredSequence()
      const sequence = String(nextSequence).padStart(3, "0")
      const generated = `${storeCode}-${productCode}-${sequence}`

      setCode(generated)
      toast.success(`Référence générée: ${generated}`)
    } catch (e: any) {
      const msg = e?.message || "Impossible de générer la référence"
      setError(msg)
      toast.error(msg)
    } finally {
      setGeneratingReference(false)
    }
  }

  const downloadImportTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        nom: "Produit exemple",
        reference: "STR-PRD-001",
        prixAchat: 12000,
        prixVente: 18500,
        quantite: 5,
        description: "Description optionnelle",
        marque: "Marque exemple",
        categorie: "Papeterie",
        unite: "piece",
        seuilAlerte: 3,
        fournisseur: "Fournisseur exemple",
        emplacement: "Rayon A",
        codeBarres: "1234567890123",
        lot: "LOT-001",
        expiration: "2026-12-31",
      },
    ])
    const guideSheet = XLSX.utils.json_to_sheet([
      {
        colonne: "nom",
        obligatoire: "oui",
        description: "Nom du produit",
      },
      {
        colonne: "reference",
        obligatoire: "non",
        description: "Reference produit. Laissez vide pour generation automatique.",
      },
      {
        colonne: "prixAchat",
        obligatoire: "non",
        description: "Prix d'achat en FCFA",
      },
      {
        colonne: "prixVente",
        obligatoire: "non",
        description: "Prix de vente en FCFA",
      },
      {
        colonne: "quantite",
        obligatoire: "non",
        description: "Quantite initiale. Defaut: 1",
      },
      {
        colonne: "marque / categorie / unite",
        obligatoire: "non",
        description: "Infos de contexte general produit",
      },
      {
        colonne: "seuilAlerte / fournisseur / emplacement",
        obligatoire: "non",
        description: "Infos de stock et de rangement",
      },
      {
        colonne: "codeBarres / lot / expiration",
        obligatoire: "non",
        description: "Traçabilite et suivi",
      },
      {
        colonne: "description",
        obligatoire: "non",
        description: "Texte libre",
      },
    ])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "ImportProduits")
    XLSX.utils.book_append_sheet(workbook, guideSheet, "Guide")
    XLSX.writeFile(workbook, "modele-import-produits.xlsx")
  }

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setError("")
      setImportSummary(null)
      setImportFileName(file.name)

      const data = await file.arrayBuffer()
      const workbook = XLSX.read(data, { type: "array" })
      const firstSheet = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[firstSheet]
      const rawRows = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, { defval: "" })

      const parsedRows = rawRows
        .map((row, index) => normalizeImportRow(row, index + 2))
        .filter((row): row is ImportRow => Boolean(row))

      if (!parsedRows.length) {
        const msg = "Aucune ligne exploitable trouvee dans le fichier Excel."
        setError(msg)
        toast.error(msg)
        setImportRows([])
        event.target.value = ""
        return
      }

      if (maxProducts !== null) {
        const allowedSlots = Math.max(0, maxProducts - Number(currentProductsCount || 0))
        if (allowedSlots <= 0) {
          const msg = "Aucun emplacement disponible pour importer de nouveaux produits."
          setError(msg)
          toast.error(msg)
          setImportRows([])
          event.target.value = ""
          return
        }

        if (parsedRows.length > allowedSlots) {
          setImportRows(parsedRows.slice(0, allowedSlots))
          toast(`${allowedSlots} ligne(s) gardees selon la limite du plan.`, { icon: "ℹ️" })
        } else {
          setImportRows(parsedRows)
        }
      } else {
        setImportRows(parsedRows)
      }

      toast.success(`${parsedRows.length} ligne(s) detectee(s) dans ${file.name}`)
    } catch (e: any) {
      const msg = e?.message || "Impossible de lire le fichier Excel"
      setError(msg)
      toast.error(msg)
      setImportRows([])
    } finally {
      event.target.value = ""
    }
  }

  const submitImportedRows = async () => {
    if (!currentStore?._id || !importRows.length) return

    try {
      setImporting(true)
      setError("")
      setImportSummary(null)

      const storeCode = buildShortcut(currentStore?.name, "STR")
      let nextSequence = await getNextStoredSequence()
      let successCount = 0
      const failures: string[] = []

      for (const row of importRows) {
        try {
          const productCode = buildShortcut(row.name, "PRD")
          const generatedReference = `${storeCode}-${productCode}-${String(nextSequence).padStart(3, "0")}`
          const identifier = row.identifier || generatedReference

          await createGeneralProduct1({
            name: row.name,
            identifier,
            buyPrice: Number(row.buyPrice || 0),
            sellPrice: Number(row.sellPrice || 0),
            quantity: Number(row.quantity || 1),
            storeId: currentStore._id,
            category: isCarStore ? "CAR" : isPropertyStore ? "PROPERTY" : "GENERAL",
            offeringType: isServiceStore ? "SERVICE" : "PRODUCT",
            vehicleCategory: row.vehicleCategory || "OCCASION",
            listingType: row.listingType || "SALE",
            pricingType: isServiceStore ? "FIXED" : undefined,
            startingPrice: 0,
            durationMinutes: 0,
            deliveryMode: isServiceStore ? "ONSITE" : undefined,
            serviceArea: isServiceStore ? String(currentStore?.serviceArea || "") : undefined,
            bookingEnabled: isServiceStore,
            featured: false,
            serviceTags: [],
            dailyRentalPrice: Number(row.dailyRentalPrice || 0),
            description: row.description,
            propertyType: row.propertyType,
            ...(isPropertyStore ? { propertyDetails: row.propertyDetails } : {}),
            district: row.district,
            bedrooms: Number(row.bedrooms || 0),
            bathrooms: Number(row.bathrooms || 0),
            areaSqm: Number(row.areaSqm || 0),
            maxGuests: Number(row.maxGuests || 0),
            monthlyRentalPrice: Number(row.monthlyRentalPrice || 0),
            amenities: row.amenities,
            nearbyAttractions: parseNearbyAttractions(row.nearbyAttractionsText),
            furnished: row.furnished,
            latitude: row.latitude,
            longitude: row.longitude,
            year: row.year,
            mileage: row.mileage,
            color: row.color,
            transmission: row.transmission,
            fuel: row.fuel,
            seats: row.seats,
            availability: row.availability,
            location: row.location,
            condition: row.condition,
            sourceName: row.sourceName,
            sourcePhone: row.sourcePhone,
            brand: row.brand,
            productCategory: row.productCategory,
            unitLabel: row.unitLabel,
            stockAlertThreshold: Number(row.stockAlertThreshold || 0),
            supplierName: row.supplierName,
            barcode: row.barcode,
            storageLocation: row.storageLocation,
            lotNumber: row.lotNumber,
            expiryDate: row.expiryDate || undefined,
            imageUrls: [],
            imageKeys: [],
            coverImageUrl: "",
            imageUrl: "",
            coverImageKey: "",
            videoUrl: "",
            videoKey: "",
          })

          successCount += 1
          nextSequence += 1
        } catch (rowError: any) {
          failures.push(`Ligne ${row.rowNumber}: ${rowError?.message || "echec d'import"}`)
        }
      }

      const failureCount = failures.length
      setImportSummary({ successCount, failureCount, failures })
      setCurrentProductsCount((current) => current + successCount)

      if (successCount > 0) {
      toast.success(`${successCount} ${isServiceStore ? "service(s)" : "produit(s)"} importe(s) avec succes`)
      }
      if (failureCount > 0) {
        toast.error(`${failureCount} ligne(s) en erreur`)
      }

      if (successCount > 0) {
        setImportRows([])
        navigate("/stock")
      }
    } catch (e: any) {
      const msg = e?.message || "Erreur pendant l'import Excel"
      setError(msg)
      toast.error(msg)
    } finally {
      setImporting(false)
    }
  }

  const handleCarImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])

    if (!files.length) {
      setCarImageFiles([])
      return
    }

    const invalidFile = files.find((file) => !file.type.startsWith("image/"))
    if (invalidFile) {
      const msg = "Tous les fichiers selectionnes doivent etre des images."
      setError(msg)
      toast.error(msg)
      event.target.value = ""
      return
    }

    const oversizedFile = files.find((file) => file.size > 15 * 1024 * 1024)
    if (oversizedFile) {
      const msg = "Chaque image ne doit pas depasser 15 MB."
      setError(msg)
      toast.error(msg)
      event.target.value = ""
      return
    }

    if (acceptsSingleOptionalImage && files.length > 1) {
      const msg = "Un seul visuel est autorise pour ce produit."
      setError(msg)
      toast.error(msg)
      event.target.value = ""
      return
    }

    setError("")
    setCarImageFiles(acceptsSingleOptionalImage ? files.slice(0, 1) : files)
  }

  const handleVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null

    if (!file) {
      setVehicleVideoFile(null)
      return
    }

    if (!file.type.startsWith("video/")) {
      const msg = "Le fichier selectionne doit etre une video."
      setError(msg)
      toast.error(msg)
      event.target.value = ""
      return
    }

    if (file.size > 250 * 1024 * 1024) {
      const msg = "La video ne doit pas depasser 250 MB."
      setError(msg)
      toast.error(msg)
      event.target.value = ""
      return
    }

    setError("")
    setVehicleVideoFile(file)
  }

  /*
   * Structured attributes must be complete before submitting. The taxonomy
   * decides which are required — shoe pointure, t-shirt size, colour — and the
   * backend re-validates the same rules, so this only spares a round-trip.
   */
  const [catalogTypes, setCatalogTypes] = useState<ProductTypeDefinition[]>([])
  useEffect(() => {
    let cancelled = false;
    taxonomyApi
      .profile()
      .then((profile) => {
        if (!cancelled) setCatalogTypes(profile.productTypes || []);
      })
      .catch(() => {
        /* Form stays usable without the taxonomy. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeTypeDefinition = useMemo(
    () => catalogTypes.find((type) => type.type === productType),
    [catalogTypes, productType]
  );

  const validateVariantStep = () => {
    if (!activeTypeDefinition) return "";

    const missing = activeTypeDefinition.required.filter((attribute) => {
      const value = variantAttributes[attribute.key];
      return value === undefined || value === null || String(value).trim() === "";
    });

    if (missing.length > 0) {
      return `Renseignez : ${missing.map((attribute) => attribute.label).join(", ")}.`;
    }
    return "";
  };

  const validateIdentityStep = () => {
    if (!name.trim() || !code.trim() || !quantity) {
      return "Nom, reference et quantite sont requis."
    }

    if (requiresBuyPrice && !buyPrice) {
      return "Le prix d'achat est requis pour ce type d'annonce."
    }

    if (requiresSellPrice && !sellPrice) {
      return "Le prix de vente est requis pour ce type d'annonce."
    }

    if (isServiceStore && pricingType === "STARTING_AT" && !startingPrice) {
      return "Le prix de depart est requis pour une offre 'A partir de'."
    }

    return ""
  }

  const validateListingStep = () => {
    if (requiresRentalPrice && !dailyRentalPrice && !(isPropertyStore && monthlyRentalPrice)) {
      return "Le prix de location est requis pour ce type d'annonce."
    }

    if (isCarStore && !vehicleCategory) {
      return "La categorie vehicule est requise."
    }

    if (isCarStore && !condition) {
      return "L'etat du vehicule est requis."
    }

    if (isPropertyStore && !propertyType) {
      return "Le type de bien est requis."
    }

    if (isServiceStore && !deliveryMode) {
      return "Le mode de prestation est requis."
    }

    return ""
  }

  const validateMediaStep = () => {
    if (minimumImages > 0 && carImageFiles.length < minimumImages) {
      return `Ajoutez au moins ${minimumImages} images du ${imageLabel}.`
    }

    return ""
  }

  const stepValidationMap: Record<StepId, () => string> = {
    identity: validateIdentityStep,
    listing: validateListingStep,
    media: validateMediaStep,
  }

  const handleNext = () => {
    const validationError = stepValidationMap[currentStepId]?.()
    if (validationError) {
      setError(validationError)
      toast.error(validationError)
      return
    }

    setError("")
    setActiveStep((current) => Math.min(current + 1, STEPS.length - 1))
  }

  const handleBack = () => {
    if (activeStep === 0) {
      navigate("/stock")
      return
    }

    setError("")
    setActiveStep((current) => Math.max(current - 1, 0))
  }

  const handleDetectLocation = async () => {
    try {
      setDetectingLocation(true)
      setError("")
      const detected = await detectCurrentPropertyLocation()
      setLatitude(String(detected.latitude))
      setLongitude(String(detected.longitude))
      if (detected.locationLabel && !locationField.trim()) setLocationField(detected.locationLabel)
      if (detected.district && !district.trim()) setDistrict(detected.district)
      toast.success("Position détectée avec succès")
    } catch (e: any) {
      const msg = e?.message || "Impossible de détecter la position"
      setError(msg)
      toast.error(msg)
    } finally {
      setDetectingLocation(false)
    }
  }

  const handleMapUrlExtract = () => {
    const parsed = parseGoogleMapsLocation(mapUrl)

    if (!parsed) {
      const msg = "Impossible d'extraire des coordonnees depuis ce lien Google Maps."
      setError(msg)
      toast.error(msg)
      return
    }

    setError("")
    setLatitude(String(parsed.latitude))
    setLongitude(String(parsed.longitude))
    if (parsed.locationLabel && !locationField.trim()) setLocationField(parsed.locationLabel)
    toast.success("Coordonnees extraites depuis le lien Google Maps")
  }

  const handleFetchNearbyAttractions = async () => {
    const parsedLatitude = Number(latitude)
    const parsedLongitude = Number(longitude)

    if (!Number.isFinite(parsedLatitude) || !Number.isFinite(parsedLongitude)) {
      const fallbackCoords = parseGoogleMapsLocation(mapUrl)

      if (!fallbackCoords) {
        const msg = "Ajoutez d'abord un geocode ou extrayez un lien Google Maps avant de charger les attractions."
        setError(msg)
        toast.error(msg)
        return
      }

      setLatitude(String(fallbackCoords.latitude))
      setLongitude(String(fallbackCoords.longitude))
    }

    const baseLatitude = Number.isFinite(parsedLatitude)
      ? parsedLatitude
      : Number(parseGoogleMapsLocation(mapUrl)?.latitude)
    const baseLongitude = Number.isFinite(parsedLongitude)
      ? parsedLongitude
      : Number(parseGoogleMapsLocation(mapUrl)?.longitude)

    try {
      setLoadingNearbyAttractions(true)
      setError("")

      const fetched = await fetchNearbyPropertyAttractions(baseLatitude, baseLongitude)
      if (!fetched.length) {
        toast("Aucune attraction trouvee a proximite.")
        return
      }

      const mergedMap = new Map<string, { name: string; category: string; distance: string }>()
      for (const item of parseNearbyAttractions(nearbyAttractionsText)) {
        mergedMap.set(`${item.name.toLowerCase()}|${item.category.toLowerCase()}`, item)
      }
      for (const item of fetched) {
        mergedMap.set(`${item.name.toLowerCase()}|${item.category.toLowerCase()}`, item)
      }

      setNearbyAttractionsText(serializeNearbyAttractions(Array.from(mergedMap.values())))
      toast.success(`${fetched.length} attraction(s) ajoutee(s) automatiquement`)
    } catch (e: any) {
      const msg = e?.message || "Impossible de recuperer les attractions a proximite"
      setError(msg)
      toast.error(msg)
    } finally {
      setLoadingNearbyAttractions(false)
    }
  }

  const summaryItems = useMemo(
    () => [
      { label: "Nom", value: name || "—" },
      { label: "Reference", value: code || "—" },
      { label: "Categorie vehicule", value: isCarStore ? VEHICLE_CATEGORY_OPTIONS.find((option) => option.value === vehicleCategory)?.label || vehicleCategory : "—" },
      { label: "Type d'annonce", value: listingType || "—" },
      { label: "Type de prix", value: isServiceStore ? SERVICE_PRICING_OPTIONS.find((option) => option.value === pricingType)?.label || pricingType : "—" },
      { label: "Prix achat", value: buyPrice ? `${Number(buyPrice).toLocaleString()} FCFA` : "Optionnel" },
      { label: "Prix vente", value: sellPrice ? `${Number(sellPrice).toLocaleString()} FCFA` : "Optionnel" },
      { label: "Duree", value: isServiceStore && durationMinutes ? `${durationMinutes} min` : "—" },
      { label: "Images", value: `${carImageFiles.length} selectionnee(s)` },
    ],
    [buyPrice, carImageFiles.length, code, durationMinutes, isCarStore, isServiceStore, listingType, name, pricingType, sellPrice, vehicleCategory]
  )

  /** Family = the product name before any variant suffix ("Nike Air Force 1"). */
  const resolvedFamilyNameFromName = () => {
    const explicit = familyName.trim();
    if (explicit) return explicit;
    const base = name.split(" — ")[0].trim();
    return base.length >= 2 ? base : "";
  };

  const submit = async () => {
    setError("")

    if (freeLimitReached) {
      const msg = "Plan FREE: limite de 20 produits atteinte. Passez à un plan payant."
      setError(msg)
      toast.error(msg)
      return
    }

    const stepErrors = [
      validateIdentityStep(),
      validateVariantStep(),
      validateListingStep(),
      validateMediaStep(),
    ].filter(Boolean)

    if (stepErrors.length > 0) {
      const firstError = stepErrors[0]
      setError(firstError)
      toast.error(firstError)
      return
    }

    try {
      setLoading(true)
      let uploadedCoverImageUrl = ""
      let uploadedImageUrl = ""
      let uploadedCoverImageKey = ""
      let uploadedImageUrls: string[] = []
      let uploadedImageKeys: string[] = []
      let uploadedVideoUrl = ""
      let uploadedVideoKey = ""

      if (carImageFiles.length) {
        setUploadingImage(true)
        const uploadResults = await uploadVehicleCarImagesToS3(carImageFiles, currentStore?._id)
        uploadedImageUrls = uploadResults
          .map((result) => result?.imageUrl || result?.coverImageUrl || "")
          .filter(Boolean)
        uploadedImageKeys = uploadResults
          .map((result) => result?.coverImageKey || "")
          .filter(Boolean)
        uploadedCoverImageUrl = uploadedImageUrls[0] || ""
        uploadedImageUrl = uploadedImageUrls[0] || ""
        uploadedCoverImageKey = uploadedImageKeys[0] || ""
      }

      if (isCarStore && vehicleVideoFile) {
        setUploadingVideo(true)
        const uploadResult = await uploadVehicleVideoToS3(vehicleVideoFile, currentStore?._id)
        uploadedVideoUrl = uploadResult?.videoUrl || ""
        uploadedVideoKey = uploadResult?.videoKey || ""
      }

      await createGeneralProduct1({
        name,
        identifier: code,
        buyPrice: Number(buyPrice || 0),
        sellPrice: Number(sellPrice || 0),
        quantity: Number(quantity),
        storeId: currentStore?._id,
        category: isCarStore ? "CAR" : isPropertyStore ? "PROPERTY" : "GENERAL",
        offeringType: isServiceStore ? "SERVICE" : "PRODUCT",
        vehicleCategory,
        listingType,
        dailyRentalPrice: Number(dailyRentalPrice || 0),
        description,
        pricingType,
        startingPrice: Number(startingPrice || 0),
        durationMinutes: Number(durationMinutes || 0),
        deliveryMode,
        serviceArea,
        bookingEnabled,
        featured,
        serviceTags: serviceTags.split(",").map((item) => item.trim()).filter(Boolean),
        propertyType,
        ...(isPropertyStore ? { propertyDetails } : {}),
        district,
        bedrooms: Number(bedrooms || 0),
        bathrooms: Number(bathrooms || 0),
        areaSqm: Number(areaSqm || 0),
        maxGuests: Number(maxGuests || 0),
        monthlyRentalPrice: Number(monthlyRentalPrice || 0),
        amenities: amenities.split(",").map((item) => item.trim()).filter(Boolean),
        nearbyAttractions: parseNearbyAttractions(nearbyAttractionsText),
        furnished,
        latitude: latitude ? Number(latitude) : null,
        longitude: longitude ? Number(longitude) : null,
        year,
        mileage,
        color,
        transmission,
        fuel,
        seats,
        availability,
        location: locationField,
        condition,
        sourceName,
        sourcePhone,
        brand,
        productCategory,
        unitLabel,
        stockAlertThreshold: Number(stockAlertThreshold || 0),
        supplierName,
        barcode,
        storageLocation,
        lotNumber,
        expiryDate: expiryDate || undefined,
        productType: productType || "GENERAL",
        ...(Object.keys(variantAttributes).length ? { variantAttributes } : {}),
        ...(resolvedFamilyNameFromName() ? { familyName: resolvedFamilyNameFromName() } : {}),
        imageUrls: uploadedImageUrls,
        imageKeys: uploadedImageKeys,
        coverImageUrl: uploadedCoverImageUrl,
        imageUrl: uploadedImageUrl,
        coverImageKey: uploadedCoverImageKey,
        videoUrl: uploadedVideoUrl,
        videoKey: uploadedVideoKey,
      })

      toast.success(`${isServiceStore ? "Service" : "Produit"} ajoute avec succes`)
      navigate("/stock")
    } catch (e: any) {
      const msg = e?.message || "Erreur serveur"
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
      setUploadingImage(false)
      setUploadingVideo(false)
    }
  }

  if (!currentStore?._id) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <p className="text-sm text-slate-600">Aucune boutique active. Revenez a la liste du stock apres avoir selectionne une boutique.</p>
      </div>
    )
  }

  return (
    <div className="min-w-0 space-y-5 overflow-x-hidden sm:space-y-6">
      <section className={`relative min-w-0 overflow-hidden rounded-3xl border border-white/40 bg-gradient-to-br ${experience.stock.heroClass} p-5 text-white shadow-xl sm:p-7`}>
        <div className={`pointer-events-none absolute -top-10 right-0 h-36 w-36 rounded-full blur-3xl ${experience.stock.heroGlowClass}`} />
        <div className="relative flex min-w-0 flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate("/stock")}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 transition hover:bg-white/20"
            >
              <ArrowLeft size={14} />
              Retour au stock
            </button>
            <p className="mt-4 inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
              <Package size={12} />
              {experience.stock.heroBadge}
            </p>
            <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{pageTitle}</h1>
            <p className="mt-2 max-w-2xl break-words text-sm text-white/85">{pageDescription}</p>
          </div>

          <div className="grid min-w-0 grid-cols-1 gap-3 sm:flex sm:w-full sm:flex-row lg:w-auto">
            <button
              type="button"
              onClick={downloadImportTemplate}
              className="inline-flex min-w-0 w-full items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-center text-sm font-medium text-white backdrop-blur-sm transition hover:bg-white/15 sm:w-auto"
            >
              <FileSpreadsheet size={16} />
              Modele Excel
            </button>
            <div className="min-w-0 rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.16em] text-white/70">Progression</p>
              <p className="mt-2 text-2xl font-semibold">{activeStep + 1}/{STEPS.length}</p>
              <p className="text-sm text-white/80">{STEPS[activeStep]?.label}</p>
            </div>
          </div>
        </div>
      </section>

      {freeLimitReached && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          Plan FREE: vous avez deja 20 produits. Passez à un plan payant pour continuer.
        </div>
      )}

      <section className="min-w-0 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
              <FileSpreadsheet size={16} />
              Import Excel
            </p>
            <p className="mt-1 max-w-2xl break-words text-sm text-slate-500">
              Suivez le modele Excel mis a jour avec les champs `marque`, `categorie`, `unite`, `seuilAlerte`, `fournisseur`, `emplacement`, `codeBarres`, `lot` et `expiration`.
            </p>
          </div>

          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch">
            <button
              type="button"
              onClick={downloadImportTemplate}
              className="inline-flex min-w-0 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-center text-sm font-medium text-slate-700 transition hover:bg-slate-50 sm:w-auto"
            >
              <FileSpreadsheet size={16} />
              Telecharger le modele
            </button>
            <label className={`inline-flex min-w-0 w-full cursor-pointer items-center justify-center gap-2 rounded-2xl px-4 py-3 text-center text-sm font-medium text-white transition sm:w-auto ${canUseExcelImport ? "bg-slate-900 hover:bg-slate-800" : "bg-slate-400"}`}>
              <Upload size={16} />
              Choisir un fichier Excel
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImportFileChange}
                disabled={!canUseExcelImport}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {importFileName && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-800">Fichier charge: {importFileName}</p>
            <p className="mt-1 text-xs text-slate-500">{importRows.length} ligne(s) prete(s) a etre importee(s).</p>
          </div>
        )}

        {importRows.length > 0 && (
          <div className="mt-4 space-y-4">
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
              <table className="min-w-[760px] w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="px-4 py-3">Ligne</th>
                    <th className="px-4 py-3">Nom</th>
                    <th className="px-4 py-3">Marque</th>
                    <th className="px-4 py-3">Categorie</th>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Quantite</th>
                    <th className="px-4 py-3">Prix vente</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.slice(0, 5).map((row) => (
                    <tr key={`${row.rowNumber}-${row.name}`} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-500">{row.rowNumber}</td>
                      <td className="px-4 py-3 font-medium text-slate-800">{row.name}</td>
                      <td className="px-4 py-3 text-slate-600">{row.brand || "-"}</td>
                      <td className="px-4 py-3 text-slate-600">{row.productCategory || "-"}</td>
                      <td className="px-4 py-3 text-slate-600">{row.identifier || "Auto"}</td>
                      <td className="px-4 py-3 text-slate-600">{row.quantity}</td>
                      <td className="px-4 py-3 text-slate-600">{Number(row.sellPrice || 0).toLocaleString()} FCFA</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            {importRows.length > 5 && (
              <p className="text-xs text-slate-500">Apercu limite aux 5 premieres lignes sur {importRows.length}.</p>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                Les lignes sans reference recevront une reference auto. Les images et videos restent a ajouter apres import.
              </p>
              <button
                type="button"
                onClick={submitImportedRows}
                disabled={!canUseExcelImport || freeLimitReached}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
              >
                {importing && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                {importing ? "Import en cours..." : `Importer ${importRows.length} produit(s)`}
              </button>
            </div>
          </div>
        )}

        {importSummary && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-800">
              Import termine: {importSummary.successCount} succes, {importSummary.failureCount} erreur(s)
            </p>
            {importSummary.failures.length > 0 && (
              <div className="mt-2 space-y-1 text-xs text-rose-700">
                {importSummary.failures.slice(0, 5).map((failure) => (
                  <p key={failure}>{failure}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div ref={stepContentRef} className="grid min-w-0 gap-6 scroll-mt-28 xl:grid-cols-[280px,minmax(0,1fr)]">
        <aside className="min-w-0 rounded-3xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex gap-3 overflow-x-auto xl:block xl:space-y-3 xl:overflow-visible">
            {STEPS.map((step, index) => {
              const isDone = index < activeStep
              const isCurrent = index === activeStep

              return (
                <button
                  key={step.id}
                  ref={(node) => {
                    stepButtonRefs.current[index] = node
                  }}
                  type="button"
                  onClick={() => setActiveStep(index)}
                  className={`flex min-w-[220px] flex-1 items-start gap-3 rounded-2xl border px-4 py-3 text-left transition xl:w-full xl:min-w-0 ${
                    isCurrent
                      ? "border-slate-900 bg-slate-900 text-white"
                      : isDone
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-white"
                  }`}
                >
                  <span className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                    isCurrent
                      ? "bg-white text-slate-900"
                      : isDone
                        ? "bg-emerald-600 text-white"
                        : "bg-white text-slate-700"
                  }`}>
                    {isDone ? <Check size={16} /> : index + 1}
                  </span>
                  <span>
                    <span className="block font-semibold">{step.label}</span>
                    <span className={`mt-1 block text-xs ${isCurrent ? "text-white/75" : "text-slate-500"}`}>
                      {step.description}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </aside>

        <section className="min-w-0 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          {currentStepId === "identity" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Identite du {itemLabel}</h2>
                <p className="mt-1 text-sm text-slate-500">Commencez par la reference, les prix et la quantite de depart.</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="text-sm text-slate-600">{isServiceStore ? "Nom du service *" : "Nom du produit *"}</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                    placeholder={isCarStore ? "Ex: Toyota RAV4 2022" : isPropertyStore ? "Ex: Residence Palm Club 2 chambres" : isServiceStore ? "Ex: Nettoyage complet de bureaux" : "Ex: Imprimante laser HP"}
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                  />
                </div>

                {/* Structured attributes: pointure, taille, couleur, matière.
                    Only shown when the store's taxonomy declares attributes for
                    this product type, so a plain grocery shop sees nothing new. */}
                <div className="md:col-span-2">
                  <VariantAttributeFields
                    productType={productType}
                    attributes={variantAttributes}
                    onChange={setVariantAttributes}
                    onProductTypeChange={setProductTypeOverride}
                    disabled={loading}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm text-slate-600">Reference / Code {isServiceStore ? "service" : "produit"} *</label>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      disabled={loading || generatingReference}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                    />
                    <button
                      type="button"
                      onClick={generateReference}
                      disabled={loading || generatingReference}
                      className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60 sm:w-auto"
                    >
                      <RefreshCcw size={16} />
                      {generatingReference ? "Generation..." : "Generer"}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Format: code boutique + code {itemLabel} + numero sequentiel.
                  </p>
                </div>

                <Field label={`${isServiceStore ? "Cout interne" : "Prix d'achat"} (FCFA)${requiresBuyPrice ? " *" : ""}`}>
                  <input
                    type="number"
                    value={buyPrice}
                    onChange={(e) => setBuyPrice(e.target.value)}
                    disabled={loading}
                    placeholder={!requiresBuyPrice ? "Optionnel" : ""}
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                  />
                </Field>

                {isCarStore && (
                  <Field label="Categorie vehicule *">
                    <select
                      value={vehicleCategory}
                      onChange={(e) => setVehicleCategory(normalizeVehicleCategory(e.target.value))}
                      disabled={loading}
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                    >
                      {VEHICLE_CATEGORY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-slate-500">
                      {VEHICLE_CATEGORY_OPTIONS.find((option) => option.value === vehicleCategory)?.description}
                    </p>
                  </Field>
                )}

                {isCarStore && (
                  <Field label="Type d'annonce *">
                    <select
                      value={listingType}
                      onChange={(e) => setListingType(e.target.value)}
                      disabled={loading}
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                    >
                      {carListingTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                {(!isCarStore || requiresSellPrice) && !isServiceStore && (
                  <Field label={`Prix de vente (FCFA)${requiresSellPrice ? " *" : ""}`}>
                    <input
                      type="number"
                      value={sellPrice}
                      onChange={(e) => setSellPrice(e.target.value)}
                      disabled={loading}
                      placeholder={!requiresSellPrice ? "Optionnel" : ""}
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                    />
                  </Field>
                )}

                <Field label={isServiceStore ? "Capacite / slots *" : "Quantite initiale *"}>
                  <input
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    disabled={loading}
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                  />
                </Field>
              </div>
            </div>
          )}

          {currentStepId === "listing" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Details et contexte</h2>
                <p className="mt-1 text-sm text-slate-500">Affinez la fiche annonce et les informations metier utiles a l'equipe.</p>
              </div>

              {(isCarStore || isPropertyStore) && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-800">{isCarStore ? "Fiche vehicule" : "Fiche bien immobilier"}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {isCarStore
                      ? "Ces informations seront conservees dans le catalogue vehicule."
                      : "Ces informations seront conservees dans le catalogue immobilier."}
                  </p>
                </div>
              )}

              {(isCarStore || isPropertyStore) && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    {isPropertyStore && (
                      <Field label="Type d'annonce *">
                        <select
                          value={listingType}
                          onChange={(e) => setListingType(e.target.value)}
                          disabled={loading}
                          className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                        >
                          <option value="SALE">Vente</option>
                          <option value="RENT">Location</option>
                          <option value="BOTH">Vente & Location</option>
                        </select>
                      </Field>
                    )}

                    <Field label={`Prix location / jour${requiresRentalPrice ? " *" : ""}`}>
                      <input
                        type="number"
                        min={0}
                        value={dailyRentalPrice}
                        onChange={(e) => setDailyRentalPrice(e.target.value)}
                        disabled={loading}
                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                      />
                    </Field>
                  </div>

                  <Field label="Description">
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={loading}
                      rows={4}
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                    />
                  </Field>
                </>
              )}

              {!isCarStore && !isPropertyStore && (
                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-semibold text-slate-800">{isServiceStore ? "Parametres de service" : "Contexte produit"}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {isServiceStore
                        ? "Structurez l'offre comme sur votre site services: prix, devis, duree, zone et mots-clés."
                        : "Ajoutez juste les informations utiles au stock, au rayon et au suivi fournisseur."}
                    </p>

                    {isGymStore && (
                      <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/80 p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">Preset Gym / Fitness</p>
                            <p className="mt-1 text-xs text-slate-500">
                              Prépare une fiche compatible avec Nearby Gyms dans GoalFit.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={applyGymPreset}
                            disabled={loading}
                            className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                          >
                            Appliquer le preset gym
                          </button>
                        </div>
                      </div>
                    )}

                    {isServiceStore && (
                      <div className="mt-4 rounded-2xl border border-cyan-100 bg-cyan-50/80 p-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <Field label="Type de prix">
                            <select
                              value={pricingType}
                              onChange={(e) => setPricingType(e.target.value)}
                              disabled={loading}
                              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                            >
                              {SERVICE_PRICING_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <p className="mt-2 text-xs text-slate-500">
                              {SERVICE_PRICING_OPTIONS.find((option) => option.value === pricingType)?.description}
                            </p>
                          </Field>

                          <Field label={pricingType === "STARTING_AT" ? "Prix de depart (FCFA) *" : "Prix affiche (FCFA)"}>
                            <input
                              type="number"
                              min={0}
                              value={pricingType === "STARTING_AT" ? startingPrice : sellPrice}
                              onChange={(e) => pricingType === "STARTING_AT" ? setStartingPrice(e.target.value) : setSellPrice(e.target.value)}
                              disabled={loading || pricingType === "QUOTE_ONLY"}
                              placeholder={pricingType === "QUOTE_ONLY" ? "Sur devis" : ""}
                              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-100 disabled:text-slate-500"
                            />
                          </Field>

                          <Field label="Duree estimee (minutes)">
                            <input
                              type="number"
                              min={0}
                              value={durationMinutes}
                              onChange={(e) => setDurationMinutes(e.target.value)}
                              disabled={loading}
                              placeholder="Ex: 90"
                              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                            />
                          </Field>

                          <Field label="Mode de prestation">
                            <select
                              value={deliveryMode}
                              onChange={(e) => setDeliveryMode(e.target.value)}
                              disabled={loading}
                              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                            >
                              {SERVICE_DELIVERY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <p className="mt-2 text-xs text-slate-500">
                              {SERVICE_DELIVERY_OPTIONS.find((option) => option.value === deliveryMode)?.description}
                            </p>
                          </Field>

                          <Field label="Zone de service">
                            <input
                              value={serviceArea}
                              onChange={(e) => setServiceArea(e.target.value)}
                              disabled={loading}
                              placeholder="Ex: Abidjan, Cocody, Marcory"
                              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                            />
                          </Field>

                          <Field label="Tags service">
                            <input
                              value={serviceTags}
                              onChange={(e) => setServiceTags(e.target.value)}
                              disabled={loading}
                              placeholder="Ex: nettoyage, bureaux, devis"
                              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                            />
                          </Field>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                          <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                            <input type="checkbox" checked={bookingEnabled} onChange={(e) => setBookingEnabled(e.target.checked)} disabled={loading} />
                            Demande de devis / reservation active
                          </label>
                          <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                            <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} disabled={loading} />
                            Mettre en avant ce service
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <Field label="Marque">
                        <input value={brand} onChange={(e) => setBrand(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                      <Field label="Categorie">
                        <input value={productCategory} onChange={(e) => setProductCategory(e.target.value)} disabled={loading} placeholder={isServiceStore ? "Ex: Nettoyage, montage, assistance" : "Ex: Gym / Fitness"} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                      <Field label="Unite de vente">
                        <input value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} disabled={loading} placeholder={isServiceStore ? "Ex: intervention, forfait, heure" : "Ex: piece, carton, pack, kg"} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                      <Field label="Seuil d'alerte stock">
                        <input type="number" min={0} value={stockAlertThreshold} onChange={(e) => setStockAlertThreshold(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                      <Field label="Fournisseur principal">
                        <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                      <Field label="Emplacement en stock">
                        <input value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)} disabled={loading} placeholder="Ex: Rayon A, Depot 2" className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                      <Field label="Code-barres / SKU secondaire">
                        <input value={barcode} onChange={(e) => setBarcode(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                      <Field label="Numero de lot">
                        <input value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                      <Field label="Date d'expiration">
                        <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                      </Field>
                    </div>
                  </div>

                  <Field label="Description">
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={loading}
                      rows={4}
                      placeholder={isServiceStore ? "Decrivez clairement la prestation, le perimetre et ce qui est inclus." : ""}
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                    />
                  </Field>

                  {isGymStore && isGymListing && (
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Localisation de la salle</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Ces champs servent au classement Nearby Gyms dans GoalFit.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleDetectLocation}
                          disabled={loading || detectingLocation}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                        >
                          <LocateFixed size={16} />
                          {detectingLocation ? "Détection..." : "Détecter la position"}
                        </button>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <Field label="Localisation visible">
                          <input
                            value={locationField}
                            onChange={(e) => setLocationField(e.target.value)}
                            disabled={loading}
                            placeholder="Ex: Cocody Angré 7e Tranche"
                            className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                          />
                        </Field>
                        <Field label="Disponibilite">
                          <select
                            value={availability}
                            onChange={(e) => setAvailability(e.target.value)}
                            disabled={loading}
                            className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                          >
                            <option value="">Selectionner</option>
                            <option value="OUI">Oui</option>
                            <option value="NON">Non</option>
                          </select>
                        </Field>
                        <Field label="Latitude">
                          <input
                            type="number"
                            step="any"
                            value={latitude}
                            onChange={(e) => setLatitude(e.target.value)}
                            disabled={loading}
                            placeholder="Ex: 5.372100"
                            className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                          />
                        </Field>
                        <Field label="Longitude">
                          <input
                            type="number"
                            step="any"
                            value={longitude}
                            onChange={(e) => setLongitude(e.target.value)}
                            disabled={loading}
                            placeholder="Ex: -3.991800"
                            className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                          />
                        </Field>
                      </div>

                      <Field label="Equipements de la salle">
                        <input
                          value={amenities}
                          onChange={(e) => setAmenities(e.target.value)}
                          disabled={loading}
                          placeholder="Cardio, musculation, douches, coach"
                          className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                        />
                      </Field>
                    </div>
                  )}
                </div>
              )}

              {isPropertyStore && (
                <>
                  <PropertyDetailsFields value={propertyDetails} onChange={setPropertyDetails} disabled={loading} />
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Type de bien *">
                      <select
                        value={propertyType}
                        onChange={(e) => setPropertyType(e.target.value)}
                        disabled={loading}
                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                      >
                        <option value="APPARTEMENT">Appartement</option>
                        <option value="RESIDENCE_MEUBLEE">Residence meublee</option>
                        <option value="VILLA">Villa</option>
                        <option value="STUDIO">Studio</option>
                        <option value="IMMEUBLE">Immeuble</option>
                        <option value="VILLA_BASSE">Villa basse</option>
                        <option value="DUPLEX">Duplex</option>
                        <option value="GROS_OEUVRE">Gros œuvre</option>
                        <option value="CLES_EN_MAIN">Clés en main</option>
                        <option value="TERRAIN">Terrain</option>
                      </select>
                    </Field>

                    <Field label="Commune / quartier">
                      <input
                        value={district}
                        onChange={(e) => setDistrict(e.target.value)}
                        disabled={loading}
                        placeholder="Ex: Cocody Riviera"
                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                      />
                    </Field>
                  </div>

                  <div className="grid gap-4 md:grid-cols-4">
                    <Field label="Chambres">
                      <input type="number" min={0} value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                    </Field>
                    <Field label="Salles de bain">
                      <input type="number" min={0} value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                    </Field>
                    <Field label="Surface m2">
                      <input type="number" min={0} value={areaSqm} onChange={(e) => setAreaSqm(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                    </Field>
                    <Field label="Voyageurs max">
                      <input type="number" min={0} value={maxGuests} onChange={(e) => setMaxGuests(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                    </Field>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Prix location / mois">
                      <input type="number" min={0} value={monthlyRentalPrice} onChange={(e) => setMonthlyRentalPrice(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                    </Field>
                    <label className="mt-7 flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                      <input type="checkbox" checked={furnished} onChange={(e) => setFurnished(e.target.checked)} disabled={loading} />
                      Bien meuble
                    </label>
                  </div>

                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Geolocalisation du bien</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Renseignez les coordonnees manuellement ou detectez la position actuelle en un clic.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleDetectLocation}
                        disabled={loading || detectingLocation}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                      >
                        <LocateFixed size={16} />
                        {detectingLocation ? "Détection..." : "Détecter la position"}
                      </button>
                    </div>

                      <div className="mt-4">
                        <Field label="Lien Google Maps / WhatsApp">
                        <div className="mt-2 flex flex-col gap-2 md:flex-row">
                          <input
                            value={mapUrl}
                            onChange={(e) => setMapUrl(e.target.value)}
                            disabled={loading}
                            placeholder="Collez un lien maps.google.com ou google.com/maps"
                            className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                          />
                          <button
                            type="button"
                            onClick={handleMapUrlExtract}
                            disabled={loading || !mapUrl.trim()}
                            className="inline-flex items-center justify-center rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                          >
                            Extraire
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          Collez un lien partage Google Maps recu sur WhatsApp pour remplir automatiquement le geocode.
                        </p>
                      </Field>
                    </div>

                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <Field label="Latitude">
                        <input
                          type="number"
                          step="any"
                          value={latitude}
                          onChange={(e) => setLatitude(e.target.value)}
                          disabled={loading}
                          placeholder="Ex: 5.372100"
                          className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                        />
                      </Field>
                      <Field label="Longitude">
                        <input
                          type="number"
                          step="any"
                          value={longitude}
                          onChange={(e) => setLongitude(e.target.value)}
                          disabled={loading}
                          placeholder="Ex: -3.991800"
                          className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                        />
                      </Field>
                    </div>
                  </div>

                  <Field label="Equipements">
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs text-slate-500">
                        Ajoutez les vrais atouts du bien ou generez une base de selling points.
                      </p>
                      <button
                        type="button"
                        onClick={handleSuggestPropertyAmenities}
                        disabled={loading}
                        className="inline-flex items-center justify-center rounded-2xl border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                      >
                        Suggerer
                      </button>
                    </div>
                    <input
                      value={amenities}
                      onChange={(e) => setAmenities(e.target.value)}
                      disabled={loading}
                      placeholder="Wi-Fi, piscine, parking"
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                    />
                  </Field>

                  <Field label="Attractions a proximite">
                    <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <p className="text-xs text-slate-500">
                        Chargez des suggestions automatiques puis ajoutez ou modifiez les lignes manuellement si besoin.
                      </p>
                      <button
                        type="button"
                        onClick={handleFetchNearbyAttractions}
                        disabled={loading || loadingNearbyAttractions}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                      >
                        <RefreshCcw size={15} />
                        {loadingNearbyAttractions ? "Chargement..." : "Charger automatiquement"}
                      </button>
                    </div>
                    <textarea
                      value={nearbyAttractionsText}
                      onChange={(e) => setNearbyAttractionsText(e.target.value)}
                      disabled={loading}
                      rows={4}
                      placeholder={"Plage d'Azuretti | Beaches | 7 min\nVillage artisanal | Artisanat | 14 min"}
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      Une attraction par ligne au format: nom | categorie | distance.
                    </p>
                  </Field>
                </>
              )}

              {(isCarStore || isPropertyStore) && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-800">Reference interne</p>
                  <p className="mt-1 text-xs text-slate-500">Ces informations restent internes a l'equipe.</p>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <Field label={isCarStore ? "Nom du proprietaire / parc auto" : "Nom du proprietaire"}>
                      <input
                        value={sourceName}
                        onChange={(e) => setSourceName(e.target.value)}
                        disabled={loading}
                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                      />
                    </Field>

                    <Field label="Numero de telephone">
                      <input
                        value={sourcePhone}
                        onChange={(e) => setSourcePhone(e.target.value)}
                        disabled={loading}
                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10"
                      />
                    </Field>
                  </div>
                </div>
              )}

              {isCarStore && (
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Annee">
                    <input value={year} onChange={(e) => setYear(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                  </Field>
                  <Field label="Kilometrage">
                    <input value={mileage} onChange={(e) => setMileage(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                  </Field>
                  <Field label="Couleur">
                    <input value={color} onChange={(e) => setColor(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                  </Field>
                  <Field label="Transmission">
                    <input value={transmission} onChange={(e) => setTransmission(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                  </Field>
                  <Field label="Carburant">
                    <input value={fuel} onChange={(e) => setFuel(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                  </Field>
                  <Field label="Places">
                    <input value={seats} onChange={(e) => setSeats(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                  </Field>
                  <Field label="Disponibilite">
                    <select value={availability} onChange={(e) => setAvailability(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10">
                      <option value="">Selectionner</option>
                      <option value="OUI">Oui</option>
                      <option value="NON">Non</option>
                    </select>
                  </Field>
                  <Field label="Localisation">
                    <input value={locationField} onChange={(e) => setLocationField(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10" />
                  </Field>
                  <Field label="Etat">
                    <select value={condition} onChange={(e) => setCondition(e.target.value)} disabled={loading} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-slate-900/10">
                      {vehicleConditionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              )}
            </div>
          )}

          {currentStepId === "media" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Media et verification</h2>
                <p className="mt-1 text-sm text-slate-500">Terminez avec les visuels obligatoires puis verifiez le resume avant enregistrement.</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="mt-0.5 rounded-xl bg-blue-50 p-2 text-blue-600">
                    <Upload size={18} />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-slate-700">
                      {isCarStore ? "Images du vehicule" : isPropertyStore ? "Images du bien" : "Image du produit"}
                    </label>
                    <p className="mt-1 text-xs text-slate-500">
                      {isCarStore
                        ? `Ajoutez au moins ${minimumImages} images. La premiere sera utilisee comme image principale.`
                        : isPropertyStore
                          ? `Ajoutez au moins ${minimumImages} images. La premiere sera utilisee comme image principale.`
                          : "Optionnel. Ajoutez une seule image pour illustrer ce produit."}
                    </p>
                    <label className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:w-auto">
                      <Upload size={16} />
                      <span>
                        {carImageFiles.length
                          ? acceptsSingleOptionalImage
                            ? "Remplacer l'image"
                            : "Remplacer les images"
                          : acceptsSingleOptionalImage
                            ? "Ajouter une image"
                            : "Ajouter les images"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple={!acceptsSingleOptionalImage}
                        onChange={handleCarImagesChange}
                        disabled={loading}
                        className="hidden"
                      />
                    </label>

                    {carImagePreviewUrls.length > 0 && (
                      <div className={`mt-4 grid gap-3 ${acceptsSingleOptionalImage ? "grid-cols-1 sm:max-w-xs" : "grid-cols-2 sm:grid-cols-4"}`}>
                        {carImagePreviewUrls.map((previewUrl, index) => (
                          <div key={`${previewUrl}-${index}`} className="overflow-hidden rounded-xl border border-slate-200">
                            <img src={previewUrl} alt={`Apercu ${imageLabel} ${index + 1}`} className="h-28 w-full object-cover" />
                            <div className="border-t border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                              {index === 0 ? "Image principale" : `Image ${index + 1}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isCarStore && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="mt-0.5 rounded-xl bg-blue-50 p-2 text-blue-600">
                    <Film size={18} />
                  </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium text-slate-700">Video 360 du vehicule</label>
                      <p className="mt-1 text-xs text-slate-500">Optionnel. La video sera envoyee via le backend au moment de l'enregistrement.</p>
                      <label className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:w-auto">
                        <Upload size={16} />
                        <span>{vehicleVideoFile ? "Remplacer la video" : "Ajouter une video 360"}</span>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleVideoChange}
                          disabled={loading}
                          className="hidden"
                        />
                      </label>
                      {vehicleVideoFile && (
                        <p className="mt-2 text-xs text-slate-500">
                          {vehicleVideoFile.name} • {(vehicleVideoFile.size / (1024 * 1024)).toFixed(1)} MB
                        </p>
                      )}
                      {vehicleVideoPreviewUrl && (
                        <video
                          src={vehicleVideoPreviewUrl}
                          controls
                          className="mt-3 max-h-56 w-full rounded-xl border border-slate-200 bg-black"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm font-semibold text-slate-800">Resume</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {summaryItems.map((item) => (
                    <div key={item.label} className="rounded-2xl border border-white bg-white p-3">
                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
                      <p className="mt-1 text-sm font-medium text-slate-800">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="sticky bottom-3 mt-8 flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:static sm:flex-row sm:items-center sm:justify-between sm:border-t sm:border-x-0 sm:border-b-0 sm:bg-transparent sm:p-0 sm:pt-6">
            <button
              type="button"
              onClick={handleBack}
              disabled={loading || uploadingImage || uploadingVideo}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 sm:w-auto"
            >
              <ArrowLeft size={16} />
              {activeStep === 0 ? "Retour" : "Etape precedente"}
            </button>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              {activeStep < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={loading || uploadingImage || uploadingVideo}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-medium text-white transition hover:bg-slate-800 disabled:opacity-60 sm:w-auto"
                >
                  Etape suivante
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={loading || uploadingImage || uploadingVideo || freeLimitReached}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-medium text-white transition hover:bg-blue-700 disabled:opacity-60 sm:w-auto"
                >
                  {(loading || uploadingImage || uploadingVideo) && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {uploadingImage ? "Upload image..." : uploadingVideo ? "Upload video..." : loading ? "Creation en cours..." : `Enregistrer ${isServiceStore ? "le service" : "le produit"}`}
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="text-sm text-slate-600">{label}</label>
      {children}
    </div>
  )
}
